/**
 * The preview's C reader: preprocessor, lexer and parser for the C codegen
 * writes (codeGenerator.js). Pure.
 *
 * Why not the expression keyboard's engine: it is built for what a learner
 * TYPES, so it reads `=` as `==` and the words and / or / not / mod as
 * operators. Codegen's C uses `=` for assignment and may name a variable `mod`.
 * This parser reads real C and builds the SAME node shapes the engine does
 * (num chr str var index member call callx neg not bitnot addr deref cast size
 * sizeType choose bin seq), plus what statements need: assign, inc, plus, init.
 * So evaluate() reads either tree.
 *
 * Anything it cannot read throws a CParseError; the preview then shows `?`.
 */

export class CParseError extends Error {
  constructor(msg, line) {
    super(msg);
    this.line = line;
    this.isCParse = true;
  }
}

// ── lexer ─────────────────────────────────────────────────────────────────

const PUNCT = [
  '<<=', '>>=', '...', '->', '++', '--', '<<', '>>', '<=', '>=', '==', '!=', '&&', '||',
  '+=', '-=', '*=', '/=', '%=', '&=', '^=', '|=', '##',
  '+', '-', '*', '/', '%', '<', '>', '=', '!', '~', '&', '|', '^', '?', ':', ';', ',', '.',
  '(', ')', '[', ']', '{', '}', '#',
];

/** Text → tokens {k:'id'|'num'|'str'|'chr'|'p', v, line}. Comments removed. */
export function lex(src, startLine = 1) {
  const s = String(src || '');
  const out = [];
  let i = 0;
  let line = startLine;
  let bol = true; // at the beginning of a line (for directives)
  while (i < s.length) {
    const ch = s[i];
    if (ch === '\n') { line += 1; i += 1; bol = true; out.push({ k: 'nl', line: line - 1 }); continue; }
    if (ch === '\\' && s[i + 1] === '\n') { i += 2; line += 1; continue; }
    if (/\s/.test(ch)) { i += 1; continue; }
    if (ch === '/' && s[i + 1] === '/') { while (i < s.length && s[i] !== '\n') i += 1; continue; }
    if (ch === '/' && s[i + 1] === '*') {
      const end = s.indexOf('*/', i + 2);
      const stop = end < 0 ? s.length : end + 2;
      for (let j = i; j < stop; j += 1) if (s[j] === '\n') line += 1;
      i = stop; continue;
    }
    const at = line;
    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < s.length && /\w/.test(s[j])) j += 1;
      out.push({ k: 'id', v: s.slice(i, j), line: at, bol });
      i = j; bol = false; continue;
    }
    if (/\d/.test(ch) || (ch === '.' && /\d/.test(s[i + 1] || ''))) {
      const m = s.slice(i).match(/^(?:0[xX][0-9a-fA-F]+|(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)[uUlLfF]*/);
      out.push({ k: 'num', v: m[0], line: at, bol });
      i += m[0].length; bol = false; continue;
    }
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      while (j < s.length && s[j] !== ch) {
        if (s[j] === '\\') j += 1;
        if (s[j] === '\n') throw new CParseError('unclosed quote', at);
        j += 1;
      }
      if (j >= s.length) throw new CParseError('unclosed quote', at);
      out.push({ k: ch === '"' ? 'str' : 'chr', v: s.slice(i + 1, j), line: at, bol });
      i = j + 1; bol = false; continue;
    }
    const p = PUNCT.find((x) => s.startsWith(x, i));
    if (!p) throw new CParseError(`cannot read "${ch}"`, at);
    out.push({ k: 'p', v: p, line: at, bol });
    i += p.length; bol = false;
  }
  return out;
}

// ── preprocessor ──────────────────────────────────────────────────────────

/**
 * Tokens with directives applied: #include noted, #define / #undef expanded,
 * #ifdef / #ifndef / #if 0|1 / #else / #endif. Newline tokens dropped.
 * → { toks, includes: [names], macros }
 */
export function preprocess(src) {
  const raw = lex(src);
  const macros = new Map();
  const includes = [];
  const toks = [];
  const conds = []; // stack of { active, done }
  const active = () => conds.every((c) => c.active);
  let i = 0;
  const lineToks = () => {
    const l = [];
    while (i < raw.length && raw[i].k !== 'nl') { l.push(raw[i]); i += 1; }
    return l;
  };
  while (i < raw.length) {
    const t = raw[i];
    if (t.k === 'nl') { i += 1; continue; }
    if (t.k === 'p' && t.v === '#' && t.bol) {
      i += 1;
      const l = lineToks();
      const d = l[0] && l[0].v;
      if (d === 'ifdef' || d === 'ifndef') {
        const def = macros.has(l[1] && l[1].v);
        conds.push({ active: d === 'ifdef' ? def : !def, done: false });
      } else if (d === 'if') {
        const v = l.slice(1).map((x) => x.v).join(' ').trim();
        if (v !== '0' && v !== '1') throw new CParseError('#if is not read here', t.line);
        conds.push({ active: v === '1' });
      } else if (d === 'else') {
        const c = conds[conds.length - 1];
        if (!c) throw new CParseError('#else without #if', t.line);
        c.active = !c.active;
      } else if (d === 'endif') {
        conds.pop();
      } else if (!active()) {
        // skipped
      } else if (d === 'include') {
        includes.push(l.slice(1).map((x) => (x.k === 'str' ? x.v : x.v)).join(''));
      } else if (d === 'define') {
        const name = l[1] && l[1].v;
        if (!name) throw new CParseError('#define without a name', t.line);
        // A function-like macro: the ( right after the name, no space.
        const fnLike = l[2] && l[2].v === '(' && l[2].line === l[1].line && isAdjacent(src, l[1], l[2]);
        if (fnLike) {
          let j = 3;
          const params = [];
          while (j < l.length && l[j].v !== ')') { if (l[j].k === 'id') params.push(l[j].v); j += 1; }
          macros.set(name, { params, body: l.slice(j + 1) });
        } else {
          macros.set(name, { body: l.slice(2) });
        }
      } else if (d === 'undef') {
        macros.delete(l[1] && l[1].v);
      } else if (d === 'pragma' || d === 'error' || d === 'warning' || d === 'line') {
        // ignored
      } else {
        throw new CParseError(`#${d} is not read here`, t.line);
      }
      continue;
    }
    if (!active()) { i += 1; continue; }
    toks.push(t);
    i += 1;
  }
  return { toks: expandMacros(toks, macros), includes, macros };
}

// The lexer does not keep columns; a function-like macro is told apart by
// finding "NAME(" in the source text of that line.
function isAdjacent(src, nameTok) {
  const lines = String(src).split('\n');
  const text = lines[nameTok.line - 1] || '';
  return new RegExp(`#\\s*define\\s+${nameTok.v}\\(`).test(text);
}

function expandMacros(toks, macros, depth = 0, hide = new Set()) {
  if (!macros.size) return toks;
  if (depth > 20) throw new CParseError('macros nest too deep', toks[0] && toks[0].line);
  const out = [];
  for (let i = 0; i < toks.length; i += 1) {
    const t = toks[i];
    const m = t.k === 'id' && !hide.has(t.v) ? macros.get(t.v) : null;
    if (!m) { out.push(t); continue; }
    const at = (x) => ({ ...x, line: t.line });
    if (!m.params) {
      const h = new Set(hide); h.add(t.v);
      out.push(...expandMacros(m.body.map(at), macros, depth + 1, h));
      continue;
    }
    if (!toks[i + 1] || toks[i + 1].v !== '(') { out.push(t); continue; }
    // Collect the arguments.
    let j = i + 2;
    let level = 0;
    const args = [[]];
    for (; j < toks.length; j += 1) {
      const x = toks[j];
      if (x.v === '(' && x.k === 'p') level += 1;
      if (x.v === ')' && x.k === 'p') { if (level === 0) break; level -= 1; }
      if (x.v === ',' && x.k === 'p' && level === 0) { args.push([]); continue; }
      args[args.length - 1].push(x);
    }
    if (j >= toks.length) throw new CParseError(`${t.v}( is not closed`, t.line);
    const body = [];
    m.body.forEach((b) => {
      const k = b.k === 'id' ? m.params.indexOf(b.v) : -1;
      if (k >= 0) body.push(...(args[k] || []).map(at));
      else if (b.k === 'p' && (b.v === '#' || b.v === '##')) throw new CParseError('# in a macro is not read here', t.line);
      else body.push(at(b));
    });
    const h = new Set(hide); h.add(t.v);
    out.push(...expandMacros(body, macros, depth + 1, h));
    i = j;
  }
  return out;
}

// ── types ─────────────────────────────────────────────────────────────────

export const T = {
  void: { k: 'void', name: 'void' },
  bool: { k: 'int', name: '_Bool', size: 1, signed: false, bool: true },
  char: { k: 'int', name: 'char', size: 1, signed: true },
  schar: { k: 'int', name: 'signed char', size: 1, signed: true },
  uchar: { k: 'int', name: 'unsigned char', size: 1, signed: false },
  short: { k: 'int', name: 'short', size: 2, signed: true },
  ushort: { k: 'int', name: 'unsigned short', size: 2, signed: false },
  int: { k: 'int', name: 'int', size: 4, signed: true },
  uint: { k: 'int', name: 'unsigned int', size: 4, signed: false },
  long: { k: 'int', name: 'long', size: 8, signed: true },
  ulong: { k: 'int', name: 'unsigned long', size: 8, signed: false },
  llong: { k: 'int', name: 'long long', size: 8, signed: true },
  ullong: { k: 'int', name: 'unsigned long long', size: 8, signed: false },
  float: { k: 'float', name: 'float', size: 4 },
  double: { k: 'float', name: 'double', size: 8 },
  ldouble: { k: 'float', name: 'long double', size: 16, long: true },
  file: { k: 'opaque', name: 'FILE' },
};
export const ptrTo = (to) => ({ k: 'ptr', to });
export const arrayOf = (of, n) => ({ k: 'array', of, n });

const BASE_WORDS = new Set(['void', 'char', 'short', 'int', 'long', 'float', 'double', 'signed', 'unsigned', '_Bool', 'bool']);
const QUALS = new Set(['const', 'volatile', 'static', 'extern', 'register', 'inline', 'auto', 'restrict']);

function baseFromWords(words, line) {
  const c = (w) => words.filter((x) => x === w).length;
  const uns = c('unsigned') > 0;
  if (c('void')) return T.void;
  if (c('_Bool') || c('bool')) return T.bool;
  if (c('char')) return uns ? T.uchar : c('signed') ? T.schar : T.char;
  if (c('short')) return uns ? T.ushort : T.short;
  if (c('float')) return T.float;
  if (c('double')) return c('long') ? T.ldouble : T.double;
  if (c('long') >= 2) return uns ? T.ullong : T.llong;
  if (c('long') === 1) return uns ? T.ulong : T.long;
  if (c('int') || c('signed') || uns) return uns ? T.uint : T.int;
  throw new CParseError(`unknown type ${words.join(' ')}`, line);
}

// ── parser ────────────────────────────────────────────────────────────────

const ASSIGN_OPS = new Set(['=', '+=', '-=', '*=', '/=', '%=', '&=', '|=', '^=', '<<=', '>>=']);
const BIN_PREC = {
  '||': 3, '&&': 4, '|': 5, '^': 6, '&': 7,
  '==': 8, '!=': 8, '<': 9, '<=': 9, '>': 9, '>=': 9,
  '<<': 10, '>>': 10, '+': 11, '-': 11, '*': 12, '/': 12, '%': 12,
};

/**
 * A translation unit → { functions: Map name → {name, type, params, body, line},
 *   globals: [decl], structs: Map tag → type, typedefs: Map, enums: Map name → value,
 *   includes }.
 */
export function parseProgram(src) {
  const { toks, includes } = preprocess(src);
  const P = new Parser(toks);
  const unit = P.unit();
  unit.includes = includes;
  return unit;
}

/** One C expression's text → tree (engine shapes). Throws CParseError. */
export function parseExpression(text, scope = {}) {
  const { toks } = preprocess(String(text));
  const P = new Parser(toks, scope);
  const e = P.expr();
  if (!P.done()) throw new CParseError(`unexpected ${P.peek().v}`, P.peek().line);
  return e;
}

/** A type's text ("struct Student *", "int[5]") → type. */
export function parseTypeName(text, scope = {}) {
  const { toks } = preprocess(String(text));
  const P = new Parser(toks, scope);
  const ty = P.typeName();
  return ty;
}

class Parser {
  constructor(toks, scope = {}) {
    this.t = toks;
    this.p = 0;
    this.typedefs = scope.typedefs || new Map();
    this.structs = scope.structs || new Map();
    this.enums = scope.enums || new Map();
    this.enumTypes = scope.enumTypes || new Map();
  }

  peek(o = 0) { return this.t[this.p + o] || { k: 'eof', v: '<end>', line: (this.t[this.t.length - 1] || {}).line || 0 }; }

  done() { return this.p >= this.t.length; }

  is(v, o = 0) { const x = this.peek(o); return (x.k === 'p' || x.k === 'id') && x.v === v; }

  eat(v) { if (this.is(v)) { this.p += 1; return true; } return false; }

  need(v) {
    if (!this.eat(v)) throw new CParseError(`expected ${v}, found ${this.peek().v}`, this.peek().line);
  }

  fail(msg) { throw new CParseError(msg, this.peek().line); }

  // Does a type start here?
  startsType(o = 0) {
    const x = this.peek(o);
    if (x.k !== 'id') return false;
    return BASE_WORDS.has(x.v) || QUALS.has(x.v) || x.v === 'struct' || x.v === 'union' || x.v === 'enum'
      || x.v === 'FILE' || x.v === 'size_t' || x.v === 'typedef' || this.typedefs.has(x.v);
  }

  // Declaration specifiers → { base, storage: Set, typedef: bool }
  specifiers() {
    const words = [];
    const storage = new Set();
    let base = null;
    let isTypedef = false;
    for (;;) {
      const x = this.peek();
      if (x.k !== 'id') break;
      if (x.v === 'typedef') { isTypedef = true; this.p += 1; continue; }
      if (QUALS.has(x.v)) { storage.add(x.v); this.p += 1; continue; }
      if (BASE_WORDS.has(x.v)) { words.push(x.v); this.p += 1; continue; }
      if (base || words.length) break;
      if (x.v === 'struct' || x.v === 'union') { base = this.structSpec(); continue; }
      if (x.v === 'enum') { base = this.enumSpec(); continue; }
      if (x.v === 'FILE') { base = T.file; this.p += 1; continue; }
      if (x.v === 'size_t') { base = T.ulong; this.p += 1; continue; }
      if (this.typedefs.has(x.v)) { base = this.typedefs.get(x.v); this.p += 1; continue; }
      break;
    }
    if (!base) {
      if (!words.length) this.fail(`expected a type, found ${this.peek().v}`);
      base = baseFromWords(words, this.peek().line);
    } else if (words.length) this.fail('a type with extra words is not read here');
    return { base, storage, isTypedef };
  }

  structSpec() {
    const kw = this.peek().v; this.p += 1;
    const union = kw === 'union';
    let tag = null;
    if (this.peek().k === 'id') { tag = this.peek().v; this.p += 1; }
    const key = `${kw} ${tag || `#${this.p}`}`;
    let ty = this.structs.get(key);
    if (!ty) { ty = { k: 'struct', union, tag, name: `${kw} ${tag || ''}`.trim(), fields: null }; this.structs.set(key, ty); }
    if (this.eat('{')) {
      const fields = [];
      while (!this.eat('}')) {
        const { base } = this.specifiers();
        do {
          const d = this.declarator(base);
          if (this.eat(':')) this.fail('bit-fields are not read here');
          fields.push({ name: d.name, type: d.type });
        } while (this.eat(','));
        this.need(';');
      }
      ty.fields = fields;
    }
    return ty;
  }

  enumSpec() {
    this.p += 1;
    let tag = null;
    if (this.peek().k === 'id') { tag = this.peek().v; this.p += 1; }
    const ty = { ...T.int, name: tag ? `enum ${tag}` : 'enum', enumTag: tag };
    if (this.eat('{')) {
      let next = 0;
      while (!this.eat('}')) {
        const name = this.peek().v; this.p += 1;
        if (this.eat('=')) {
          const e = this.conditional();
          const v = constValue(e, this.enums);
          if (v === null) this.fail('an enum value that is not a plain number');
          next = v;
        }
        this.enums.set(name, next);
        next += 1;
        if (!this.eat(',')) { this.need('}'); break; }
      }
    }
    if (tag) this.enumTypes.set(tag, ty);
    return ty;
  }

  // A declarator over a base type → { name, type, params? (for a function) }
  declarator(base, { abstract = false } = {}) {
    let ptrs = 0;
    while (this.is('*')) { this.p += 1; ptrs += 1; while (this.peek().k === 'id' && QUALS.has(this.peek().v)) this.p += 1; }
    let name = null;
    let inner = null; // a parenthesised declarator: (*fp)(int)
    if (this.is('(') && (this.is('*', 1))) {
      this.p += 1;
      inner = this.declarator(T.void, { abstract });
      this.need(')');
    } else if (this.peek().k === 'id' && !this.startsTypeWord()) {
      name = this.peek().v; this.p += 1;
    } else if (!abstract) {
      this.fail(`expected a name, found ${this.peek().v}`);
    }
    let ty = base;
    for (let k = 0; k < ptrs; k += 1) ty = ptrTo(ty);
    // Suffixes: arrays and a parameter list.
    const dims = [];
    let params = null;
    for (;;) {
      if (this.eat('[')) {
        if (this.eat(']')) { dims.push(null); continue; }
        const e = this.expr();
        this.need(']');
        dims.push(e);
        continue;
      }
      if (this.is('(')) {
        this.p += 1;
        params = this.paramList();
        continue;
      }
      break;
    }
    for (let k = dims.length - 1; k >= 0; k -= 1) ty = { k: 'array', of: ty, nExpr: dims[k], n: dims[k] ? constValue(dims[k], this.enums) : null };
    if (params) ty = { k: 'fn', ret: ty, params: params.list, variadic: params.variadic };
    if (inner) {
      // (*fp)(args): a pointer to the function type built above.
      let t2 = ty;
      let d = inner.type;
      const stack = [];
      while (d && d !== T.void) { stack.push(d); d = d.k === 'ptr' ? d.to : null; }
      for (let k = 0; k < stack.length; k += 1) t2 = ptrTo(t2);
      return { name: inner.name, type: t2 };
    }
    return { name, type: ty, params: params ? params.list : null };
  }

  startsTypeWord() {
    const x = this.peek();
    return x.k === 'id' && (BASE_WORDS.has(x.v) || x.v === 'struct' || x.v === 'union' || x.v === 'enum');
  }

  paramList() {
    const list = [];
    let variadic = false;
    if (this.eat(')')) return { list, variadic };
    if (this.is('void') && this.is(')', 1)) { this.p += 2; return { list, variadic }; }
    for (;;) {
      if (this.eat('...')) { variadic = true; this.need(')'); break; }
      const { base } = this.specifiers();
      const d = this.declarator(base, { abstract: true });
      let ty = d.type;
      if (ty.k === 'array') ty = ptrTo(ty.of); // a list parameter is a pointer
      if (ty.k === 'fn') ty = ptrTo(ty);
      list.push({ name: d.name, type: ty });
      if (this.eat(',')) continue;
      this.need(')');
      break;
    }
    return { list, variadic };
  }

  typeName() {
    const { base } = this.specifiers();
    const d = this.declarator(base, { abstract: true });
    return d.type;
  }

  unit() {
    const functions = new Map();
    const globals = [];
    const protos = new Map();
    while (!this.done()) {
      if (this.eat(';')) continue;
      const line = this.peek().line;
      const spec = this.specifiers();
      if (this.eat(';')) continue; // struct/enum definition alone
      const first = this.declarator(spec.base);
      if (first.type.k === 'fn' && this.is('{')) {
        const body = this.block();
        functions.set(first.name, { name: first.name, type: first.type, params: first.type.params, body, line, storage: spec.storage });
        continue;
      }
      const decls = this.restOfDeclaration(spec, first, line);
      decls.forEach((d) => {
        if (d.type.k === 'fn') protos.set(d.name, d);
        else if (!d.isTypedef) globals.push(d);
      });
    }
    return {
      functions, globals, protos, structs: this.structs, typedefs: this.typedefs, enums: this.enums, enumTypes: this.enumTypes,
    };
  }

  // After the first declarator: initialisers, more declarators, the ';'.
  restOfDeclaration(spec, first, line) {
    const out = [];
    let d = first;
    for (;;) {
      const one = { name: d.name, type: d.type, line, storage: spec.storage, isTypedef: spec.isTypedef };
      if (spec.isTypedef) this.typedefs.set(d.name, d.type);
      else if (this.eat('=')) one.init = this.initializer();
      out.push(one);
      if (!this.eat(',')) break;
      d = this.declarator(spec.base);
    }
    this.need(';');
    return out;
  }

  initializer() {
    if (this.is('{')) {
      const line = this.peek().line;
      this.p += 1;
      const items = [];
      while (!this.eat('}')) {
        if (this.is('.') || this.is('[')) this.fail('designated initialisers are not read here');
        items.push(this.initializer());
        if (!this.eat(',')) { this.need('}'); break; }
      }
      return { t: 'init', items, line };
    }
    return this.assignment();
  }

  // ── statements ──
  block() {
    const line = this.peek().line;
    this.need('{');
    const items = [];
    while (!this.eat('}')) {
      if (this.done()) this.fail('a { is not closed');
      items.push(this.statement());
    }
    return { s: 'block', items, line, endLine: this.t[this.p - 1].line };
  }

  statement() {
    const x = this.peek();
    const line = x.line;
    if (x.k === 'p' && x.v === '{') return this.block();
    if (x.k === 'p' && x.v === ';') { this.p += 1; return { s: 'empty', line }; }
    if (x.k === 'id') {
      switch (x.v) {
        case 'if': {
          this.p += 1; this.need('(');
          const c = this.expr(); this.need(')');
          const then = this.statement();
          const els = this.eat('else') ? this.statement() : null;
          return { s: 'if', c, then, else: els, line };
        }
        case 'while': {
          this.p += 1; this.need('(');
          const c = this.expr(); this.need(')');
          return { s: 'while', c, body: this.statement(), line };
        }
        case 'do': {
          this.p += 1;
          const body = this.statement();
          this.need('while'); this.need('(');
          const c = this.expr(); this.need(')'); this.need(';');
          return { s: 'do', c, body, line };
        }
        case 'for': {
          this.p += 1; this.need('(');
          let init = null;
          if (this.eat(';')) init = null;
          else if (this.startsType()) init = this.declaration();
          else { init = { s: 'expr', e: this.expr(), line }; this.need(';'); }
          const c = this.is(';') ? null : this.expr();
          this.need(';');
          const step = this.is(')') ? null : this.expr();
          this.need(')');
          return { s: 'for', init, c, step, body: this.statement(), line };
        }
        case 'switch': {
          this.p += 1; this.need('(');
          const e = this.expr(); this.need(')');
          return { s: 'switch', e, body: this.statement(), line };
        }
        case 'case': {
          this.p += 1;
          const v = this.conditional();
          this.need(':');
          return { s: 'case', v, line };
        }
        case 'default': this.p += 1; this.need(':'); return { s: 'default', line };
        case 'break': this.p += 1; this.need(';'); return { s: 'break', line };
        case 'continue': this.p += 1; this.need(';'); return { s: 'continue', line };
        case 'return': {
          this.p += 1;
          const e = this.is(';') ? null : this.expr();
          this.need(';');
          return { s: 'return', e, line };
        }
        case 'goto': this.fail('goto is not read here'); break;
        default:
          if (this.startsType()) return this.declaration();
      }
    }
    const e = this.expr();
    this.need(';');
    return { s: 'expr', e, line };
  }

  declaration() {
    const line = this.peek().line;
    const spec = this.specifiers();
    if (this.eat(';')) return { s: 'empty', line };
    const first = this.declarator(spec.base);
    const decls = this.restOfDeclaration(spec, first, line);
    return { s: 'decl', decls: decls.filter((d) => !d.isTypedef && d.type.k !== 'fn'), line };
  }

  // ── expressions ──
  expr() {
    const line = this.peek().line;
    const first = this.assignment();
    if (!this.is(',')) return first;
    const items = [first];
    while (this.eat(',')) items.push(this.assignment());
    return { t: 'seq', items, line };
  }

  assignment() {
    const l = this.conditional();
    const x = this.peek();
    if (x.k === 'p' && ASSIGN_OPS.has(x.v)) {
      this.p += 1;
      const r = this.assignment();
      return { t: 'assign', op: x.v, l, r, line: x.line };
    }
    return l;
  }

  conditional() {
    const c = this.binary(3);
    if (this.eat('?')) {
      const a = this.expr();
      this.need(':');
      const b = this.conditional();
      return { t: 'choose', c, a, b };
    }
    return c;
  }

  binary(min) {
    let l = this.unary();
    for (;;) {
      const x = this.peek();
      const pr = x.k === 'p' ? BIN_PREC[x.v] : undefined;
      if (pr === undefined || pr < min) break;
      this.p += 1;
      const r = this.binary(pr + 1);
      l = { t: 'bin', op: x.v, l, r };
    }
    return l;
  }

  isCastHere() {
    return this.is('(') && this.startsType(1) && this.peek(1).v !== 'typedef';
  }

  unary() {
    const x = this.peek();
    if (x.k === 'p') {
      switch (x.v) {
        case '-': this.p += 1; return { t: 'neg', x: this.unary() };
        case '+': this.p += 1; return { t: 'plus', x: this.unary() };
        case '!': this.p += 1; return { t: 'not', x: this.unary() };
        case '~': this.p += 1; return { t: 'bitnot', x: this.unary() };
        case '&': this.p += 1; return { t: 'addr', x: this.unary() };
        case '*': this.p += 1; return { t: 'deref', x: this.unary() };
        case '++': case '--': this.p += 1; return { t: 'inc', op: x.v, pre: true, x: this.unary() };
        case '(':
          if (this.isCastHere()) {
            this.p += 1;
            const ty = this.typeName();
            this.need(')');
            if (this.is('{')) this.fail('compound literals are not read here');
            return { t: 'cast', ty, x: this.unary() };
          }
          break;
        default: break;
      }
    }
    if (x.k === 'id' && x.v === 'sizeof') {
      this.p += 1;
      if (this.isCastHere()) {
        this.p += 1;
        const ty = this.typeName();
        this.need(')');
        return { t: 'sizeType', ty };
      }
      return { t: 'size', x: this.unary() };
    }
    return this.postfix(this.primary());
  }

  postfix(x0) {
    let x = x0;
    for (;;) {
      if (this.eat('[')) { const idx = this.expr(); this.need(']'); x = { t: 'index', x, idx }; continue; }
      if (this.is('(')) {
        this.p += 1;
        const args = [];
        if (!this.eat(')')) {
          for (;;) { args.push(this.assignment()); if (this.eat(',')) continue; this.need(')'); break; }
        }
        x = x.t === 'var' ? { t: 'call', fn: x.v, args, line: x.line } : { t: 'callx', f: x, args };
        continue;
      }
      if (this.is('.') || this.is('->')) {
        const arrow = this.peek().v === '->';
        this.p += 1;
        const n = this.peek();
        if (n.k !== 'id') this.fail('expected a field name');
        this.p += 1;
        x = { t: 'member', x, name: n.v, arrow };
        continue;
      }
      if (this.is('++') || this.is('--')) { const op = this.peek().v; this.p += 1; x = { t: 'inc', op, pre: false, x }; continue; }
      break;
    }
    return x;
  }

  primary() {
    const x = this.peek();
    if (x.k === 'num') { this.p += 1; return { t: 'num', v: x.v }; }
    if (x.k === 'chr') { this.p += 1; return { t: 'chr', v: x.v }; }
    if (x.k === 'str') {
      let v = '';
      while (this.peek().k === 'str') { v += this.peek().v; this.p += 1; }
      return { t: 'str', v };
    }
    if (x.k === 'id') {
      this.p += 1;
      if (this.enums.has(x.v)) return { t: 'num', v: String(this.enums.get(x.v)), enumName: x.v };
      return { t: 'var', v: x.v, line: x.line };
    }
    if (this.eat('(')) {
      const e = this.expr();
      this.need(')');
      return { t: 'group', x: e };
    }
    return this.fail(`unexpected ${x.v}`);
  }
}

/** A constant integer expression's value (array sizes, enum values), or null. */
export function constValue(e, enums = new Map()) {
  if (!e) return null;
  switch (e.t) {
    case 'num': {
      const s = e.v.replace(/[uUlL]+$/, '');
      if (/[.eE]/.test(s) && !/^0[xX]/.test(s)) return null;
      return /^0[xX]/.test(s) ? parseInt(s, 16) : /^0\d/.test(s) ? parseInt(s, 8) : Number(s);
    }
    case 'group': return constValue(e.x, enums);
    case 'var': return enums.has(e.v) ? enums.get(e.v) : null;
    case 'neg': { const v = constValue(e.x, enums); return v === null ? null : -v; }
    case 'bin': {
      const a = constValue(e.l, enums);
      const b = constValue(e.r, enums);
      if (a === null || b === null) return null;
      switch (e.op) {
        case '+': return a + b;
        case '-': return a - b;
        case '*': return a * b;
        case '/': return b ? Math.trunc(a / b) : null;
        case '%': return b ? a % b : null;
        case '<<': return a * 2 ** b;
        default: return null;
      }
    }
    default: return null;
  }
}
